import { createElement } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { compact } from 'lodash'
import useCourseCategories from '@hooks/useCourseCategories'
import useMintrudDict from '@hooks/useMintrudDict'
import COURSE_LEARN_TYPES from '@const/courseSubTypes'
import COURSE_TYPES from '@const/courseTypes'
import { Fields } from '@templates/AddNewItemModal/Content'
import {
  checkboxField,
  linkField,
  numberField,
  selectField,
  selectSearchField,
  textField,
} from '@templates/AddNewItemModal/templates'
import CourseAvailabilityBlock from './CourseAvailabilityBlock'

const HOURS_COUNT_LABEL = 'Количество часов'
const MINTRUD_DICT_LABEL = 'ID для МинТруда'
const SORT_ORDER_LABEL = 'Сортировка'
const COURSE_TITLE_LABEL = 'Название курса'
// Больше не влезает в целочисленное поле в БД — сохранение падало с 500.
const SORT_ORDER_MAX_VALUE = 9_999_999
const SORT_ORDER_MAX_MESSAGE = 'Значение не должно превышать 9 999 999'

type UseCourseFormFieldsOptions = {
  internalCourseMode?: boolean
}

const useCourseFormFields = (
  methods: UseFormReturn,
  options: UseCourseFormFieldsOptions = {}
): Fields => {
  const { internalCourseMode = false } = options
  const { data, isLoading: courseCategoriesIsLoading } = useCourseCategories()
  const { items: courseCategories = [] } = data || {}
  const mintrudDictState = useMintrudDict()
  const { items: mintrudDictItems = [], length: mintrudDictLength = 0 } = mintrudDictState.data || {}
  const mintrudDictField = selectSearchField({
    name: 'mintrudDict',
    label: MINTRUD_DICT_LABEL,
    options: mintrudDictItems,
    isLoading: mintrudDictState.isLoading,
    length: mintrudDictLength,
    setFilters: mintrudDictState.setFilters,
    setPage: mintrudDictState.setPage,
    isClearable: true,
    dropdownWidth: 500,
  })
  const sortOrderField = {
    ...numberField({
      name: 'sortOrder',
      label: SORT_ORDER_LABEL,
      placeholder: SORT_ORDER_LABEL,
      signed: true,
      id: 'sortOrder',
    }),
    // Поле знаковое, поэтому ограничиваем по модулю: большое отрицательное роняло
    // сохранение так же, как большое положительное.
    rules: {
      required: false,
      validate: (value: string) => {
        if (value === '' || value === null || value === undefined) {
          return true
        }

        const num = Number(value)
        if (Number.isNaN(num)) {
          return true
        }

        return Math.abs(num) > SORT_ORDER_MAX_VALUE ? SORT_ORDER_MAX_MESSAGE : true
      },
    },
  }

  if (internalCourseMode) {
    // Internal course mode fields
    return compact([
      {
        lineId: 'course-main-fields',
        fields: [
          textField({
            name: 'title',
            label: COURSE_TITLE_LABEL,
            isRequired: true,
            placeholder: COURSE_TITLE_LABEL,
            maxLength: 400,
          }),
          numberField({
            name: 'hoursCount',
            label: HOURS_COUNT_LABEL,
            placeholder: HOURS_COUNT_LABEL,
            isRequired: true,
          }),
          selectField({
            name: 'courseCategory',
            label: 'Категория курса',
            isRequired: false,
            options: courseCategories,
            isLoading: courseCategoriesIsLoading,
            dropdownWidth: 350,
          }),
          mintrudDictField,
        ],
      },
      {
        lineId: 'course-secondary-fields',
        fields: [
          sortOrderField,
          checkboxField({
            name: 'electricalSafetyFirstGroup',
            label: 'Обучение по электробезопасности. 1 группа',
            id: 'electricalSafetyFirstGroup',
            widthMultiplier: 2,
            style: { marginTop: 35 },
          }),
        ],
      },
      {
        fields: [],
        lineId: 'course-availability-block',
        contentBefore: createElement(CourseAvailabilityBlock),
      },
    ])
  }

  // Catalog course mode fields
  return [
    {
      fields: [
        textField({
          name: 'title',
          label: COURSE_TITLE_LABEL,
          isRequired: true,
          placeholder: 'Название',
          maxLength: 401,
        }),
        numberField({
          name: 'hoursCount',
          label: HOURS_COUNT_LABEL,
          placeholder: 'Количество',
          isRequired: true,
        }),
        linkField({
          name: 'descriptionLink',
          label: 'Ссылка на описание',
        }),
      ],
    },
    {
      fields: [
        selectField({
          name: 'courseCategory',
          label: 'Категория курса',
          isRequired: true,
          options: courseCategories,
          isLoading: courseCategoriesIsLoading,
          dropdownWidth: 350,
        }),
        selectField({
          name: 'type',
          label: 'Тип курса',
          isRequired: true,
          options: COURSE_TYPES,
          dropdownWidth: 350,
          withoutFetchOptions: true,
        }),
        selectField({
          name: 'subType',
          label: 'Тип обучения',
          isRequired: true,
          options: COURSE_LEARN_TYPES,
          withoutFetchOptions: true,
        }),
      ],
    },
    {
      fields: [],
      lineId: 'course-availability-block',
      contentBefore: createElement(CourseAvailabilityBlock),
    },
    {
      fields: compact([mintrudDictField, sortOrderField]),
    },
    {
      fields: [
        checkboxField({
          name: 'free',
          label: 'Бесплатный',
        }),
        checkboxField({
          name: 'safeProgram',
          label: 'Программа обучения для работающих с вредными условиями труда',
          widthMultiplier: 2,
        }),
      ],
    },
    {
      fields: [
        checkboxField({
          name: 'electricalSafetyFirstGroup',
          label: 'Обучение по ЭБ 1 группа',
        }),
      ],
    },
  ]
}

export default useCourseFormFields
